<template>
    <section id="social-media-channels" class="section-white container mx-auto max-w-5xl">
        <!-- Title -->
        <div class="flex items-center justify-center mb-10 gap-4 flex-col text-center md:flex-row md:text-left">
            <i class="fi fi-ss-badge-check text-6xl text-blue mt-3"></i>
            <h2 class="text-2xl font-bold uppercase">{{ $t('socialMedia.title') }}</h2>
        </div>

        <!-- Desktop -->
        <div class="hidden lg:block">
            <div class="flex justify-between mb-10">
                <div v-for="platform in platformList" :key="platform" class="cursor-pointer" @click="select(platform)">
                    <div
                        class="rounded-lg h-28 w-28 flex flex-col items-center justify-center p-4 bg-gray-50 transition-all duration-300 ease-in-out shadow-lg hover:shadow-2xl"
                        :style="selected === platform ? shadowStyle(platform) : {}"
                    >
                        <img :src="iconsByPlatform[platform]" :alt="platform + ' Logo'" class="w-8 h-8 mb-2" />
                        <span class="text-sm font-semibold">{{ platform }}</span>
                    </div>
                </div>
            </div>
            <Transition name="fade" mode="out-in">
                <div v-if="selected" :key="selected" class="grid gap-4" :class="selectedCount > 1 ? 'grid-cols-2' : 'grid-cols-1'">
                    <a
                        v-for="entry in platformsData[selected]"
                        :key="entry.handle"
                        :href="entry.url"
                        target="_blank"
                        class="flex items-center bg-gray-50 p-4 rounded-lg w-full hover:bg-gray-100 transition duration-300 ease-in-out"
                    >
                        <img
                            :src="selected === 'YouTube' ? entry.avatar || defaultAvatar : $asset(entry.avatar) || $asset(defaultAvatar)"
                            alt="Logo"
                            title="Logo"
                            class="w-16 h-16 rounded-full mr-4 object-cover transition-all duration-300 ease-in-out"
                            @error="onAvatarError"
                        />
                        <div>
                            <div class="text-lg font-semibold">{{ entry.name }}</div>
                            <div class="text-gray-500 text-sm">{{ entry.handle }}</div>
                        </div>
                    </a>
                </div>
            </Transition>
        </div>

        <!-- Mobile -->
        <div class="lg:hidden">
            <div class="flex justify-center mb-6 gap-4">
                <div v-for="platform in platformList" :key="platform" class="cursor-pointer" @click="select(platform)">
                    <div
                        class="rounded-lg p-2 flex flex-col items-center justify-center transition-all duration-300 ease-in-out bg-gray-50 shadow-lg hover:shadow-xl"
                        :style="selected === platform ? shadowStyle(platform) : {}"
                    >
                        <img :src="iconsByPlatform[platform]" :alt="platform + ' Logo'" class="w-8 h-8" />
                    </div>
                </div>
            </div>
            <div class="text-lg font-bold mb-2 text-center">{{ selected }}</div>
            <Transition name="fade" mode="out-in">
                <div v-if="selected" :key="selected" class="grid gap-4 px-4" :class="selectedCount > 1 ? 'sm:grid-cols-2 grid-cols-1' : 'grid-cols-1'">
                    <a
                        v-for="entry in platformsData[selected]"
                        :key="entry.handle"
                        :href="entry.url"
                        target="_blank"
                        class="flex flex-col md:flex-row items-center bg-gray-50 p-4 rounded-lg w-full hover:bg-gray-100 transition-colors duration-200"
                    >
                        <img
                            :src="entry.avatar || defaultAvatar"
                            alt="Logo"
                            class="w-12 h-12 rounded-full mb-2 md:mb-0 md:mr-3 object-cover transition-all duration-300 ease-in-out"
                            @error="onAvatarError"
                        />
                        <div class="text-center md:text-left w-full">
                            <div class="text-base font-semibold">{{ entry.name }}</div>
                            <div class="text-gray-500 text-xs">{{ entry.handle }}</div>
                        </div>
                    </a>
                </div>
            </Transition>
        </div>
    </section>
</template>

<script setup>
import { ref, computed } from 'vue';

const platformList = ['YouTube', 'Instagram', 'X', 'TikTok', 'WhatsApp', 'LinkedIn', 'Facebook'];

const iconsByPlatform = {
    YouTube: 'https://cdn.jsdelivr.net/npm/simple-icons@v9/icons/youtube.svg',
    Instagram: 'https://cdn.jsdelivr.net/npm/simple-icons@v9/icons/instagram.svg',
    X: 'https://cdn.jsdelivr.net/npm/simple-icons@v9/icons/x.svg',
    TikTok: 'https://cdn.jsdelivr.net/npm/simple-icons@v9/icons/tiktok.svg',
    WhatsApp: 'https://cdn.jsdelivr.net/npm/simple-icons@v9/icons/whatsapp.svg',
    LinkedIn: 'https://cdn.jsdelivr.net/npm/simple-icons@v9/icons/linkedin.svg',
    Facebook: 'https://cdn.jsdelivr.net/npm/simple-icons@v9/icons/facebook.svg'
};

const platformsData = {
    YouTube: [
        {
            name: 'HKCM',
            handle: '@hkcm',
            url: 'https://www.youtube.com/@hkcm',
            avatar: 'https://yt3.googleusercontent.com/zlybLDt4kIQCGIJTiKq_kbzdldlLJ8ibeJukLmUxPpyjDjAPsWVP99YxYmmtbpFzxe9gh4-_QQ=s120-c-k-c0x00ffffff-no-rj'
        },
        {
            name: 'Phantom by HKCM',
            handle: '@phantombyhkcm',
            url: 'https://www.youtube.com/@phantombyhkcm',
            avatar: 'https://yt3.googleusercontent.com/SKxvW7lSi3MI1HK-CVInsGBj6NzIGNP89UNCcEt4ZeW-YKYSt2lke7HgGFTsbbLbRQ_3OBScww=s120-c-k-c0x00ffffff-no-rj'
        },
        {
            name: 'Charts & Cars by Philip Klinkmüller',
            handle: '@ChartsCarsbyPhilipKlinkmüller',
            url: 'https://www.youtube.com/@ChartsCarsbyPhilipKlinkmüller',
            avatar: 'https://yt3.googleusercontent.com/f07j95TRw6WyxkR_-WwzRkyh6lEYrV31ohUa32MXc3va1gtSJvdQ29tlsiHqSHQJaO4p3DMcZA=s120-c-k-c0x00ffffff-no-rj'
        },
        {
            name: 'HKCM GLOBAL',
            handle: '@HKCM_GLOBAL',
            url: 'https://www.youtube.com/@HKCM_GLOBAL',
            avatar: 'https://yt3.googleusercontent.com/WRTWbGoklkuNoIUjDOuvvFoAZ2kVG8dguiFtCmW4nSqpakwJ8kKTFi7KcyIGcUPSIZ864HhfxQ=s120-c-k-c0x00ffffff-no-rj'
        },
        {
            name: 'HKCM Shorts',
            handle: '@hkcmshorts_official',
            url: 'https://www.youtube.com/@hkcmshorts_official',
            avatar: 'https://yt3.googleusercontent.com/5KGeXEEcpCkWCAerWpxXIxbOQfGL4vPD6J1LDF9KIw4d8uoNG12-jvNNjpQJ1H_ZwxxcK1aXkvM=s120-c-k-c0x00ffffff-no-rj'
        }
    ],
    Instagram: [
        {
            name: 'HKCM',
            handle: '@hkc_management',
            url: 'https://www.instagram.com/hkc_management/',
            avatar: '/hkcm/images/socials/profilepictures/profilepicture_instagram_hkcm.jpg'
        },
        {
            name: 'Phantom by HKCM',
            handle: '@phantom_by_hkcm_official',
            url: 'https://www.instagram.com/phantom_by_hkcm_official/',
            avatar: '/hkcm/images/socials/profilepictures/profilepicture_instagram_phantom.jpg'
        },
        {
            name: 'Philip Hopf (Privat)',
            handle: '@philiphopf',
            url: 'https://www.instagram.com/philiphopf/',
            avatar: '/hkcm/images/socials/profilepictures/profilepicture_instagram_ph.jpg'
        },
        {
            name: 'Philip Klinkmüller (Privat)',
            handle: '@philipklinkmueller',
            url: 'https://www.instagram.com/philipklinkmueller/',
            avatar: '/hkcm/images/socials/profilepictures/profilepicture_instagram_pk.jpg'
        },
        {
            name: 'HKCM GLOBAL',
            handle: '@hkcm_global',
            url: 'https://www.instagram.com/hkcm_global/',
            avatar: '/hkcm/images/socials/profilepictures/profilepicture_instagram_global.jpg'
        }
    ],
    X: [
        {
            name: 'HKCM',
            handle: '@HKCM1',
            url: 'https://x.com/HKCM1',
            avatar: '/hkcm/images/socials/profilepictures/profilepicture_instagram_hkcm.jpg'
        },
        {
            name: 'HKCM NEWS',
            handle: '@HKCM1',
            url: 'https://x.com/hkcmnews',
            avatar: '/hkcm/images/socials/profilepictures/profilepicture_whatsapp.jpeg'
        },
        {
            name: 'Phantom by HKCM',
            handle: '@phantom_by_hkcm',
            url: 'https://x.com/Phantom_by_HKCM',
            avatar: '/hkcm/images/socials/profilepictures/profilepicture_x_phantom.jpg'
        },
        {
            name: 'HKCM_GLOBAL',
            handle: '@hkcm_global',
            url: 'https://x.com/hkcm_global',
            avatar: '/hkcm/images/socials/profilepictures/profilepicture_instagram_global.jpg'
        }
    ],
    TikTok: [
        {
            name: 'HKCM',
            handle: '@hkcmanagement',
            url: 'https://www.tiktok.com/@hkcmanagement',
            avatar: '/hkcm/images/socials/profilepictures/profilepicture_instagram_hkcm.jpg'
        }
    ],
    WhatsApp: [
        {
            name: 'HKCM NEWS',
            handle: 'Nachrichten, Analysen und Kommentare',
            url: 'https://whatsapp.com/channel/0029VaYbplbAojYwXY8Lfp0M',
            avatar: '/hkcm/images/socials/profilepictures/profilepicture_whatsapp.jpeg'
        }
    ],
    LinkedIn: [
        {
            name: 'HKCM',
            handle: 'Unsere Analyse. Ihr Handelserfolg.',
            url: 'https://www.linkedin.com/company/hkcm',
            avatar: '/hkcm/images/socials/profilepictures/profilepicture_instagram_hkcm.jpg'
        }
    ],
    Facebook: [
        {
            name: 'HKCM',
            handle: 'Unsere Analyse. Ihr Handelserfolg.',
            url: 'https://www.facebook.com/p/HKCM-100065608264874/',
            avatar: '/hkcm/images/socials/profilepictures/profilepicture_instagram_hkcm.jpg'
        }
    ]
};

const shadowColors = {
    YouTube: ['#FF0000', '#C00000'],
    Instagram: ['#F712BE', '#FDA802'],
    TikTok: ['#3EF7EF', '#F90150'],
    X: ['#000000', '#232323'],
    WhatsApp: ['#53CE60', '#36B339'],
    LinkedIn: ['#0866C2', '#0866C2'],
    Facebook: ['#0966FF', '#0966FF']
};

const defaultAvatar = '/hkcm/images/logo-small-on-dark-background.svg';
const selected = ref('YouTube');

function select(platform) {
    selected.value = platform;
}

const selectedCount = computed(() => platformsData[selected.value]?.length ?? 0);

function shadowStyle(platform) {
    const [top, bottom] = shadowColors[platform] || ['rgba(0,0,0,0.2)', 'rgba(0,0,0,0.2)'];

    return {
        boxShadow: `0 -3px 15px ${top}, 0 3px 15px ${bottom}`
    };
}

function onAvatarError(event) {
    event.target.src = defaultAvatar;
}
</script>
<style scoped>
.fade-expand-enter-active,
.fade-expand-leave-active {
    transition: all 0.4s ease;
}

.fade-expand-enter-from,
.fade-expand-leave-to {
    opacity: 0.3;
    transform: translateY(5px);
}

.fade-expand-enter-to,
.fade-expand-leave-from {
    opacity: 1;
    transform: translateY(0);
}
</style>
